import { forwardRef } from 'react';

import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { AriaLabelingProps } from '../../core/types/a11y-props.js';
import { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import { DataTestId } from '../../core/types/data-props.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { WithChildren } from '../../core/types/with-children.js';
import { Flex } from '../../layouts/flex/Flex.js';

//#region Typings
/**
 * Accepted properties for the Menu Prefix.
 * @public
 */
export interface MenuPrefixProps
  extends WithChildren,
    AriaLabelingProps,
    StylingProps,
    DataTestId,
    BehaviorTrackingProps {}
//#endregion

/**
 * The `Menu.Prefix` component allows you to specify any content that should
 * be prefixed in the menu item. The `Menu.Prefix` component is only valid
 * within the `Menu.Item`, `Menu.Link`, `Menu.SubTrigger`, and `Menu.Intent` elements.
 * @example
 * ```tsx
 * <Menu.Item onSelect={() => {}}>
 *   <Menu.Prefix>
 *     <WhatsNewIcon />
 *   </Menu.Prefix>
 *   Label
 * </Menu.Item>
 * ```
 * @public
 */
export const Prefix = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  MenuPrefixProps
>(
  (
    {
      children,
      className,
      style,
      'data-testid': dataTestId,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'aria-describedby': ariaDescribedBy,
      'aria-details': ariaDetails,
      ...remainingProps
    },
    forwardedRef,
  ) => {
    const [behavioralTrackingProps] =
      useBehavioralTrackingProps(remainingProps);

    return (
      <Flex
        ref={forwardedRef}
        className={className}
        style={style}
        data-testid={dataTestId}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        aria-details={ariaDetails}
        {...behavioralTrackingProps}
      >
        {children}
      </Flex>
    );
  },
);

(Prefix as typeof Prefix & { displayName: string }).displayName = 'Menu.Prefix';
